import axios from "axios";
import Head from "next/head";
import { useRouter } from "next/router";
import { type ReactElement, useCallback, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import {
  type AuthResponseError,
  type LoginResponse,
} from "~/components/auth/types";
import { Container } from "~/components/layout/Container";
import Layout from "~/components/layout/Layout";
import Button from "~/components/ui/Button/Button";
import Input from "~/components/ui/Input/Input";
import Loading from "~/components/ui/Loading/Loading";
import { NotificationContext } from "~/context/NotificationContext";
import { env } from "~/env.mjs";
import { validatePassword } from "~/utils";

interface FormFields {
  password: string;
  passwordConfirm: string;
}

const Recovery = () => {
  const { showNotification } = useContext(NotificationContext);
  const [isLoading, setIsLoading] = useState(false);

  const { query, push } = useRouter();

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>();

  const onSubmit = useCallback(
    async ({ password, passwordConfirm }: FormFields) => {
      setIsLoading(true);

      await axios
        .post<LoginResponse>(
          `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v1/auth/email/reset-password`,
          {
            password: password,
            passwordConfirm: passwordConfirm,
            token: query?.token || "",
          }
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .then(async (res) => {
          showNotification(
            "Your password has been reset successfully. Please log in.",
            "success"
          );

          await push("/");
        })
        .catch((err) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const error: AuthResponseError = err?.response?.data || {};

          showNotification(
            error?.message ||
              error?.errorMessage ||
              "Resetting your password failed. Please try again.",
            "error"
          );
        });

      setIsLoading(false);
    },
    [push, query?.token, showNotification]
  );

  return (
    <>
      <Head>
        <title>RFOX VALT | Reset password</title>
      </Head>
      <Container>
        <div className="pt-10">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mx-auto w-fit space-y-8"
          >
            <div className="w-72 space-y-4 text-base text-light-100 md:w-96">
              <h2 className="text-xl font-bold">Reset your password</h2>
              <div className="space-y-2">
                <label>New password:</label>
                <Input
                  type="password"
                  placeholder="New password"
                  {...register("password", {
                    required: { value: true, message: "Password is required." },
                    validate: (password) =>
                      validatePassword(password) ||
                      "Password must be at least 8 characters long and contain a combination of uppercase and lowercase letters, numbers, and special characters.",
                  })}
                />
                <span className="text-xs text-red-500">
                  {errors.password?.message}
                </span>
              </div>
              <div className="space-y-2">
                <label>Confirm password:</label>
                <Input
                  type="password"
                  placeholder="Confirm password"
                  {...register("passwordConfirm", {
                    required: { value: true, message: "Password is required." },
                    validate: (passwordConfirm) =>
                      passwordConfirm === getValues("password") ||
                      "Confirm password does not match.",
                  })}
                />
                <span className="text-xs text-red-500">
                  {errors.passwordConfirm?.message}
                </span>
              </div>
            </div>

            <div className="flex-reverse flex">
              <Button
                type="submit"
                width={"full"}
                disabled={!query?.token || isLoading}
              >
                {isLoading ? <Loading size={"small"} /> : "Reset password"}
              </Button>
            </div>
          </form>
        </div>
      </Container>
    </>
  );
};

Recovery.getLayout = function getLayout(page: ReactElement) {
  return <Layout publicAccess={true}>{page}</Layout>;
};

export default Recovery;
